'use client'

import React, { useEffect, useState } from 'react'
import Text from '../../Text'
import styles from './styles.module.css'
import { CircularProgress, Grow } from '@mui/material'
import { useRegionAddOns } from '../../RegionSurfaces'
import InfoIcon from '../../icons/Info'
import { PALETTE } from '../../../styles/constants'
import { formatPrice } from '../../../utils/formats'
import { useDetectMobile } from '../../../utils/hooks/useDetectMobile'

function AddOnsTable({ backgroundColor = { value: PALETTE.gray10 }, textColor = { value: PALETTE.gray80 } }) {
  // Runtime CMS-driven colours -> inline style (matches former styled `${props}`).
  const colorStyle = { backgroundColor: backgroundColor.value, color: textColor.value }

  const [isMobile] = useDetectMobile(991)

  const [infoShownCode, setInfoShownCode] = useState(null)
  const [showInfoTimeoutId, setShowInfoTimeoutId] = useState(null)

  // Overmind→server-props (Phase A2.2): add-ons come from the SSR-threaded
  // <RegionSurfacesProvider> (byte-identical to the store, which stays hydrated),
  // with an Overmind fallback for any non-page entry point.
  const { addOns, ensureRequested } = useRegionAddOns()
  useEffect(() => {
    ensureRequested()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // #418: gate the loader on THIS section's OWN data (vrsAddOns), NOT the global
  // state.isLoading flag. vrsAddOns is SSR-hydrated whenever the page renders this
  // section (buildSurfaceMutations), so it is PRESENT on the client's first render
  // — SSR and client-first render content identically. The loader shows only when
  // the data is genuinely absent (SAME on both sides). The old `state.isLoading ||`
  // term emitted a spinner into the SSR HTML whenever any OTHER section's fetch
  // flipped the global flag (the latency-only #418 cascade).
  if (!addOns || !addOns.length) {
    return (
      <div className={styles.loading} style={colorStyle}>
        <CircularProgress color="inherit"/>
      </div>
    )
  }

  const allLicenses = Object.keys(addOns[0].licenseTypeInfo)
  const addOnsByCatalogue = {}

  addOns.forEach(a => {
    const addOns = addOnsByCatalogue[a.catalogue.name] || []
    addOns.push(a)
    addOnsByCatalogue[a.catalogue.name] = addOns
  })

  // Reproduces the former `grid-template-columns` middleColNum interpolation.
  const rowStyle = { '--middle-col-num': allLicenses.length }

  const getAddOnInfoIcon = (addOn) => {
    return (
      <>
        <Grow in={addOn.code === infoShownCode} timeout={{ enter: 250, exit: 250 }} style={{ transformOrigin: 'bottom left' }}>
          <div className={styles.addOnInfo}>
            <Text type="button-1">{addOn.description}</Text>
          </div>
        </Grow>
        <div
          className={styles.iconWrapper}
          onMouseEnter={() => {
            if (!isMobile) {
              const timeoutId = setTimeout(() => setInfoShownCode(addOn.code), 250)
              setShowInfoTimeoutId(timeoutId)
            }
          }}
          onMouseLeave={() => {
            if (!isMobile) {
              clearTimeout(showInfoTimeoutId)
              setInfoShownCode(null)
            }
          }}
          onClick={() => isMobile && setInfoShownCode(infoShownCode === addOn.code ? null : addOn.code)}
        >
          <InfoIcon/>
        </div>
      </>
    )
  }

  return (
      <div className={styles.wrapper} style={colorStyle}>
        <Grow in={true} timeout={{ enter: 500 }} >
          <div className={styles.tableWrapper}>
            <div className={styles.row} style={rowStyle}>
              { renderTableCell({ text: 'Name', isTitle: true })}
              { allLicenses.map(l => renderTableCell({ text: l, isTitle: true, key: l }))}
              { renderTableCell({ text: 'Price per month', isTitle: true })}
            </div>
            {
              Object.entries(addOnsByCatalogue).map(([catalogue, addOns]) =>
                  <>
                    <div className={styles.row} key={catalogue}>
                      { renderTableCell({ text: catalogue, isSubtitle: true, key: catalogue }) }
                    </div>
                    {
                      addOns.map(a =>
                        <div className={styles.row} style={rowStyle} key={a.code}>
                          { renderTableCell({ text: formatAddOnName(a.name), textIcon: getAddOnInfoIcon(a), key: a.code }) }
                          { allLicenses.map(l => renderTableCell({ text: a.licenseTypeInfo[l], key: l + a.licenseTypeInfo[l] })) }
                          { renderTableCell({ text: formatPrice(a.price.monthly), key: a.code }) }
                        </div>
                      )
                    }
                  </>
              )
            }
          </div>
        </Grow>
      </div>
  )
}

const renderTableCell = ({ text, textIcon, isTitle, isSubtitle, key }) => {
  return (
    <>
      <div className={isTitle ? `${styles.tableCell} ${styles.tableCellTitle}` : styles.tableCell} key={key}>
        { textIcon && textIcon }
        <Text type={isTitle ? 'h4' : (isSubtitle ? 'h5' : 'body-short-1')}>{text}</Text>
      </div>
    </>
  )
}

const formatAddOnName = (name) => {
  return name.replace('Add-on: ', '')
}

export default AddOnsTable
